"use client";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { FALLBACK_LOCALE, Locales } from "../i18n/settings";

interface LocaleContextValue {
  locale: Locales;
  setLocale: (locale: Locales) => void;
}

const Context = createContext<LocaleContextValue | null>(null);
const NOOP = () => {};

function LocaleController({
  children,
  value,
}: {
  children: React.ReactNode;
  value: Locales;
}) {
  const [locale, setLocaleState] = useState<Locales>(value);

  const setLocale = useCallback((newLocale: Locales) => {
    setLocaleState(newLocale);
  }, []);

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  return (
    <Context.Provider value={{ locale, setLocale }}>
      {children}
    </Context.Provider>
  );
}

export function LocaleProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: Locales;
}) {
  const parent = useContext(Context);
  // Nested copies steal setLocale and overwrite html dir after the inner update.
  if (parent) return children;
  return <LocaleController value={value}>{children}</LocaleController>;
}

export function useLocale() {
  return useContext(Context)?.locale ?? FALLBACK_LOCALE;
}

export function useSetLocale() {
  return useContext(Context)?.setLocale ?? NOOP;
}
